import {
  Box,
  ChakraProps,
  Image,
  ImageProps,
  Stack,
  VStack,
} from '@chakra-ui/react';
import { useMultiStyleConfig } from '@chakra-ui/react';
import React from 'react';

import { WLH2V2, WLHeading } from '..';
import { isStaticImageDef } from '../Utils';

export interface CalloutProps {
  heading?: string;
  overline?: string;
  content: React.ReactElement | undefined;
  image?: StaticImageDef | React.ReactNode;
  actions: React.ReactNode;
  variant: 'primary' | 'search' | 'image';
  colorScheme: 'darkblue' | 'lightblue';
  overlay: 'NONE' | 'TOP' | 'BOTTOM';
}

interface CalloutImageProps {
  sx?: ChakraProps['sx'];
  image: StaticImageDef | React.ReactNode;
  height: ImageProps['height'];
}

interface CalloutContentProps {
  sx?: ChakraProps['sx'];
  children: React.ReactElement | undefined;
}

interface ActionGroupProps {
  sx?: ChakraProps['sx'];
  children: React.ReactNode | React.ReactNode[];
}

const CalloutContent: React.FC<CalloutContentProps> = ({ children, sx }) => {
  if (!children) {
    return null;
  }
  return <Box sx={sx}>{children}</Box>;
};

const ActionGroup: React.FC<ActionGroupProps> = ({ children, sx }) => {
  if (!children) {
    return null;
  }

  return (
    <Stack direction={{ base: 'column', lg: 'row' }} sx={sx}>
      {children}
    </Stack>
  );
};

const CalloutImage: React.FC<CalloutImageProps> = ({ image, height, sx }) => {
  if (!image) {
    return null;
  }
  if (isStaticImageDef(image)) {
    return (
      <Image
        src={image.src}
        srcSet={image.srcset}
        sizes={image.sizes}
        alt={image.description}
        height={height}
      />
    );
  }

  return (
    <Box as="figure" sx={sx} height={height}>
      {image}
    </Box>
  );
};

const Callout: React.FC<CalloutProps> = ({
  heading,
  overline,
  content,
  image,
  colorScheme = 'darkblue',
  variant = 'primary',
  overlay = 'NONE',
  actions,
}) => {
  const Styles = useMultiStyleConfig('Callout', { variant, colorScheme });

  return (
    <Stack
      as="article"
      paddingY={{ base: '18px', lg: 0 }}
      paddingX={'42px'}
      borderRadius={{ base: '16px', lg: 'none' }}
      sx={Styles.callout}
      width={{ base: 'full', lg: '100vw' }}
      maxW={'1536px'}
      mt={{ base: overlay === 'TOP' ? '-44px' : 0, lg: 0 }}
    >
      <Stack
        as="article"
        sx={Styles.content}
        direction={{
          base: 'column',
          lg: variant === 'image' ? 'column' : 'row',
        }}
      >
        <VStack as="header" sx={Styles.header}>
          {overline && (
            <WLHeading variant="overline" colorScheme={colorScheme}>
              {overline}
            </WLHeading>
          )}
          <WLHeading
            as={overline ? 'h3' : 'h2'}
            colorScheme={colorScheme === `darkblue` ? `white` : undefined}
          >
            {heading}
          </WLHeading>
        </VStack>
        <VStack as="section" sx={Styles.columnTwo}>
          <CalloutContent sx={Styles.copy}>{content}</CalloutContent>
          <ActionGroup sx={Styles.actionGroup}>{actions}</ActionGroup>
        </VStack>
      </Stack>

      {image && <CalloutImage sx={Styles.image} image={image} height="auto" />}
    </Stack>
  );
};

export default Callout;
